import {
  getSupportTicketLeafFilters,
  resolveSupportTicketFilterName,
  SupportTicketFilterNames,
  type SupportTicketFilterName,
} from "@redotech/effect-api-shared/schema/support-ticket-filters";
import {
  AI_ASSIGNEE_EXCLUSION_FILTER,
  SPAM_TAG_EXCLUSION_FILTER,
} from "@redotech/redo-support-v2/query/default-views";
import {
  filtersAreEqual,
  type AdvancedFilterData,
  type FilterGroup,
} from "@redotech/redo-views-common/advanced-filters/advanced-filter-data";
import { FilterOperator } from "@redotech/redo-views-common/advanced-filters/generic-advanced-filter-data";

const HIDDEN_FILTERS = new Set<SupportTicketFilterName>([
  SupportTicketFilterNames.STATUS,
  SupportTicketFilterNames.NUM_DRAFTS,
  SupportTicketFilterNames.NUM_SENT_MESSAGES,
]);

const REDO_DEFAULT_FILTERS = [
  SPAM_TAG_EXCLUSION_FILTER,
  AI_ASSIGNEE_EXCLUSION_FILTER,
];

function isHiddenFilter(filter: AdvancedFilterData): boolean {
  const resolvedName = resolveSupportTicketFilterName(filter.name);
  return resolvedName !== null && HIDDEN_FILTERS.has(resolvedName);
}

export function isRedoDefaultSupportTicketFilter(
  filter: AdvancedFilterData,
): boolean {
  return REDO_DEFAULT_FILTERS.some((defaultFilter) =>
    filtersAreEqual(filter, defaultFilter),
  );
}

export function getSupportTicketFiltersForDisplay(
  filterGroup: FilterGroup,
): AdvancedFilterData[] {
  return getSupportTicketLeafFilters(filterGroup).filter(
    (filter) => !isHiddenFilter(filter),
  );
}

/** Collapses equal clauses, so an edit onto a default cannot double up. */
function dedupeFilters(filters: AdvancedFilterData[]): AdvancedFilterData[] {
  return filters.filter(
    (filter, index) =>
      filters.findIndex((other) => filtersAreEqual(filter, other)) === index,
  );
}

export function partitionSupportTicketFiltersForDisplay(
  filterGroup: FilterGroup,
  explicitFilterGroup?: FilterGroup,
): { defaultFilters: AdvancedFilterData[]; userFilters: AdvancedFilterData[] } {
  const explicitFilters = explicitFilterGroup
    ? getSupportTicketLeafFilters(explicitFilterGroup)
    : [];
  const defaultFilters: AdvancedFilterData[] = [];
  const userFilters: AdvancedFilterData[] = [];
  for (const filter of getSupportTicketFiltersForDisplay(filterGroup)) {
    (isRedoDefaultSupportTicketFilter(filter) &&
    !explicitFilters.some((explicitFilter) =>
      filtersAreEqual(filter, explicitFilter),
    )
      ? defaultFilters
      : userFilters
    ).push(filter);
  }
  return { defaultFilters, userFilters };
}

function toExplicitFilterGroup(
  filters: AdvancedFilterData[],
): FilterGroup | undefined {
  return filters.length === 0
    ? undefined
    : { operator: FilterOperator.AND, clauses: filters };
}

/** Records a user edit only when it becomes identical to a Redo default. */
export function replaceExplicitSupportTicketFilter(
  explicitFilterGroup: FilterGroup | undefined,
  previousFilter: AdvancedFilterData,
  nextFilter: AdvancedFilterData,
): FilterGroup | undefined {
  const retained = (
    explicitFilterGroup ? getSupportTicketLeafFilters(explicitFilterGroup) : []
  ).filter((filter) => !filtersAreEqual(filter, previousFilter));
  if (isRedoDefaultSupportTicketFilter(nextFilter)) {
    retained.push(nextFilter);
  }
  return toExplicitFilterGroup(retained);
}

/** Records an explicitly added filter when it happens to equal a default. */
export function addExplicitSupportTicketFilter(
  explicitFilterGroup: FilterGroup | undefined,
  filter: AdvancedFilterData,
): FilterGroup | undefined {
  if (!isRedoDefaultSupportTicketFilter(filter)) {
    return explicitFilterGroup;
  }
  const existing = explicitFilterGroup
    ? getSupportTicketLeafFilters(explicitFilterGroup)
    : [];
  return existing.some((existingFilter) =>
    filtersAreEqual(existingFilter, filter),
  )
    ? explicitFilterGroup
    : toExplicitFilterGroup([...existing, filter]);
}

/** Drops provenance for filters that are no longer part of the active view. */
export function retainExplicitSupportTicketFilters(
  explicitFilterGroup: FilterGroup | undefined,
  displayedFilters: AdvancedFilterData[],
): FilterGroup | undefined {
  const retained = (
    explicitFilterGroup ? getSupportTicketLeafFilters(explicitFilterGroup) : []
  ).filter((explicitFilter) =>
    displayedFilters.some((filter) => filtersAreEqual(filter, explicitFilter)),
  );
  return toExplicitFilterGroup(retained);
}

export function replaceSupportTicketFiltersForDisplay(
  filterGroup: FilterGroup,
  displayedFilters: AdvancedFilterData[],
): FilterGroup {
  const hiddenFilters =
    getSupportTicketLeafFilters(filterGroup).filter(isHiddenFilter);
  return {
    operator: FilterOperator.AND,
    clauses: [...hiddenFilters, ...dedupeFilters(displayedFilters)],
  };
}
